import { DeleteIcon, MoreHorizontalIcon } from '@blocksuite/icons/rc';
import type { MenuProps } from '@pique/component';
import { IconButton, Input, Menu, Scrollable } from '@pique/component';
import {
  attendeeExistsByEmail,
  normalizeEmail,
} from '@pique/core/modules/properties/services/attendee-id-utils';
import { AttendeesService } from '@pique/core/modules/properties/services/attendees-service';
import type { SerializedAttendee } from '@pique/core/modules/properties/services/schema';
import { deduplicateAttendees } from '@pique/core/modules/properties/services/utils';
import { posthog } from '@pique/core/posthog';
import { useI18n } from '@pique/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { clamp } from 'lodash-es';
import { UsersIcon } from 'lucide-react';
import { nanoid } from 'nanoid';
import type { HTMLAttributes, PropsWithChildren } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { adaptSerializedAttendees, AttendeeAvatars } from './attendee-avatars';
import * as styles from './attendees-inline-editor.css';
import type { MenuItemOption } from './menu-items';
import { renderMenuItemOptions } from './menu-items';

interface AttendeesEditorProps {
  pageId: string;
  readonly?: boolean;
  focusedIndex?: number;
}

interface InlineAttendeesListProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>,
    Omit<AttendeesEditorProps, 'onOptionsChange'> {
  onRemove?: () => void;
}

// Attendee item component for display
export const AttendeeItem = ({
  attendee,
  onRemoved,
  focused,
}: {
  attendee: SerializedAttendee;
  onRemoved?: () => void;
  focused?: boolean;
}) => {
  return (
    <div
      className={styles.attendeeItemContainer}
      data-focused={focused ? 'true' : undefined}
    >
      <AttendeeAvatars
        attendees={[
          {
            id: attendee.id,
            name: attendee.name,
            email: attendee.email,
            avatar: attendee.avatar,
          },
        ]}
        maxDisplay={1}
        size="large"
      />
      {onRemoved && (
        <IconButton
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onRemoved();
          }}
          size="16"
          data-testid="remove-attendee-button"
        >
          <DeleteIcon />
        </IconButton>
      )}
    </div>
  );
};

// Temporary Attendee Item for creation UI
export const TempAttendeeItem = ({
  name,
  email,
}: {
  name: string;
  email: string;
}) => {
  return (
    <div className={styles.attendeeItemContainer}>
      <AttendeeAvatars
        attendees={[
          {
            name,
            email,
          },
        ]}
        maxDisplay={1}
        size="large"
      />
      <div className={styles.attendeeDetails}>
        <div className={styles.attendeeName}>{name || email}</div>
        {name && email && <div className={styles.attendeeEmail}>{email}</div>}
      </div>
    </div>
  );
};

export const InlineAttendeesList = ({
  pageId,
  readonly,
  children,
  focusedIndex,
  onRemove,
}: PropsWithChildren<InlineAttendeesListProps>) => {
  const attendeesService = useService(AttendeesService);
  const allAttendeesMap = useLiveData(attendeesService.attendees$);
  const attendees = useMemo(
    () => deduplicateAttendees(allAttendeesMap[pageId] || []),
    [allAttendeesMap, pageId]
  );

  return (
    <div
      className={styles.inlineAttendeesContainer}
      data-testid="inline-attendees-list"
    >
      {attendees.length > 0 ? (
        <>
          <div className={styles.avatarContainer}>
            <AttendeeAvatars
              attendees={adaptSerializedAttendees(attendees)}
              maxDisplay={3}
              size="small"
              className={styles.inlineAvatarStack}
            />
          </div>
          {!readonly &&
            attendees.map((attendee, idx) => {
              const onRemoved = () => {
                attendeesService.removePageAttendee(pageId, attendee.id);
                posthog.track('remove_attendee', { context: 'attendees_editor', source: 'inline' });
                onRemove?.();
              };
              return (
                <div
                  key={attendee.id}
                  className={styles.compactAttendeeItem}
                  data-focused={focusedIndex === idx ? 'true' : undefined}
                >
                  <span title={attendee.email}>
                    {attendee.name ||
                      (attendee.email
                        ? attendee.email.split('@')[0]
                        : 'Unknown')}
                  </span>
                  <IconButton
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onRemoved();
                    }}
                    size="12"
                    data-testid="remove-attendee-button"
                    className={styles.removeAttendeeButton}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              );
            })}
        </>
      ) : null}
      {children}
    </div>
  );
};

export const EditAttendeeMenu = ({
  attendeeId,
  pageId,
  children,
}: PropsWithChildren<{
  attendeeId: string;
  pageId: string;
}>) => {
  const t = useI18n();
  const attendeesService = useService(AttendeesService);
  const allAttendeesMap = useLiveData(attendeesService.attendees$);
  const attendees = useMemo(
    () => deduplicateAttendees(allAttendeesMap[pageId] || []),
    [allAttendeesMap, pageId]
  );
  const attendee = attendees.find(a => a.id === attendeeId);

  const menuProps = useMemo(() => {
    const options: MenuItemOption[] = [];

    if (!attendee) return { items: [] };

    options.push(
      <Input
        defaultValue={attendee.name}
        onBlur={(e: FocusEvent & { currentTarget: HTMLInputElement }) => {
          const newName = e.currentTarget.value.trim();
          if (newName && newName !== attendee.name) {
            attendeesService.updatePageAttendee(pageId, attendeeId, {
              name: newName,
            });
            posthog.track('update_attendee', { context: 'attendees_editor', field: 'name' });
          }
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const newName = e.currentTarget.value.trim();
            if (newName && newName !== attendee.name) {
              attendeesService.updatePageAttendee(pageId, attendeeId, {
                name: newName,
              });
              posthog.track('update_attendee', { context: 'attendees_editor', field: 'name' });
            }
          }
          e.stopPropagation();
        }}
        placeholder={t['Untitled']()}
      />
    );

    options.push(
      <Input
        defaultValue={attendee.email}
        onBlur={(e: FocusEvent & { currentTarget: HTMLInputElement }) => {
          const newEmail = e.currentTarget.value.trim();
          if (newEmail && newEmail !== attendee.email) {
            attendeesService.updatePageAttendee(pageId, attendeeId, {
              email: newEmail,
            });
            posthog.track('update_attendee', { context: 'attendees_editor', field: 'email' });
          }
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const newEmail = e.currentTarget.value.trim();
            if (newEmail && newEmail !== attendee.email) {
              attendeesService.updatePageAttendee(pageId, attendeeId, {
                email: newEmail,
              });
              posthog.track('update_attendee', { context: 'attendees_editor', field: 'email' });
            }
          }
          e.stopPropagation();
        }}
        placeholder="Email"
      />
    );

    options.push('-');

    options.push({
      text: t['Delete'](),
      icon: <DeleteIcon />,
      type: 'danger',
      onClick() {
        attendeesService.removePageAttendee(pageId, attendeeId);
        posthog.track('remove_attendee', { context: 'attendees_editor', source: 'editor' });
      },
    });

    options.push({
      text: attendee.present ? 'Mark as Absent' : 'Mark as Present',
      onClick() {
        attendeesService.updatePageAttendee(pageId, attendeeId, {
          present: !attendee.present,
        });
        posthog.track('update_attendee', { context: 'attendees_editor', field: 'present' });
      },
    });

    const items = renderMenuItemOptions(options);

    return {
      contentOptions: {
        onClick(e: React.MouseEvent) {
          e.stopPropagation();
        },
      },
      items,
    } satisfies Partial<MenuProps>;
  }, [attendee, attendeeId, attendeesService, pageId, t]);

  return <Menu {...menuProps}>{children}</Menu>;
};

// Define the type for attendee option (existing or create new)
type AttendeeOption =
  | SerializedAttendee
  | { readonly create: true; readonly name: string; readonly email: string };

const isCreateNewAttendee = (
  attendeeOption: AttendeeOption
): attendeeOption is {
  readonly create: true;
  readonly name: string;
  readonly email: string;
} => {
  return 'create' in attendeeOption;
};

export const AttendeesEditor = ({ pageId, readonly }: AttendeesEditorProps) => {
  const attendeesService = useService(AttendeesService);
  const [inputValue, setInputValue] = useState('');

  // Split input by space to extract potential name and email
  const inputParts = inputValue.split(/\s+/);
  const potentialEmail = inputParts.find(part => part.includes('@')) || '';
  const potentialName = inputParts
    .filter(part => part !== potentialEmail)
    .join(' ');

  const allAttendeesMap = useLiveData(attendeesService.attendees$);
  const attendees = useMemo(
    () => deduplicateAttendees(allAttendeesMap[pageId] || []),
    [allAttendeesMap, pageId]
  );
  const allAttendees = useMemo(() => {
    const allPageAttendees: SerializedAttendee[] = [];
    for (const pageAttendees of Object.values(allAttendeesMap) as SerializedAttendee[][]) {
      allPageAttendees.push(...pageAttendees);
    }
    return deduplicateAttendees(allPageAttendees);
  }, [allAttendeesMap]);

  // Filter attendees based on input
  const filteredAttendees = useMemo(() => {
    if (!inputValue.trim()) return allAttendees;

    return allAttendees.filter(attendee => {
      const searchLower = inputValue.toLowerCase();
      return (
        attendee.name.toLowerCase().includes(searchLower) ||
        attendee.email.toLowerCase().includes(searchLower)
      );
    });
  }, [allAttendees, inputValue]);

  const exactMatch = filteredAttendees.find(
    attendee =>
      (potentialEmail &&
        normalizeEmail(attendee.email) === normalizeEmail(potentialEmail)) ||
      attendee.name.toLowerCase() === inputValue.toLowerCase()
  );

  const showCreateView =
    filteredAttendees.length === 0 &&
    inputValue.trim().length > 0 &&
    !exactMatch;

  const attendeeOptions: AttendeeOption[] = useMemo(() => {
    if (showCreateView) {
      return [{ create: true, name: potentialName, email: potentialEmail }];
    }
    return filteredAttendees;
  }, [filteredAttendees, showCreateView, potentialName, potentialEmail]);

  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [focusedInlineIndex, setFocusedInlineIndex] = useState<number>(
    attendees.length
  );

  const safeFocusedIndex = clamp(focusedIndex, -1, attendeeOptions.length - 1);
  const safeInlineFocusedIndex = clamp(
    focusedInlineIndex,
    -1,
    attendees.length
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const onSelectAttendeeOption = useCallback(
    (attendeeOption: AttendeeOption) => {
      if (isCreateNewAttendee(attendeeOption)) {
        const newAttendee: SerializedAttendee = {
          id: nanoid(),
          name: attendeeOption.name || attendeeOption.email.split('@')[0] || attendeeOption.email,
          email: attendeeOption.email,
          present: true,
        };
        attendeesService.addPageAttendee(pageId, newAttendee);
        posthog.track('add_attendee', { context: 'attendees_editor', is_new_attendee: true });
      } else {
        const isAlreadyAdded = attendeeExistsByEmail(
          attendees,
          attendeeOption.email
        );

        if (!isAlreadyAdded) {
          attendeesService.addPageAttendee(pageId, attendeeOption);
          posthog.track('add_attendee', { context: 'attendees_editor', is_new_attendee: false });
        } else {
          const existing = attendees.find(
            a =>
              normalizeEmail(a.email) === normalizeEmail(attendeeOption.email)
          );
          if (existing) {
            attendeesService.removePageAttendee(pageId, existing.id);
            posthog.track('remove_attendee', { context: 'attendees_editor', source: 'editor' });
          }
        }
      }

      setInputValue('');
      focusInput();
      setFocusedIndex(-1);
      setFocusedInlineIndex(attendees.length + 1);
    },
    [attendees, attendeesService, focusInput, pageId]
  );

  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const selectedOption = attendeeOptions[safeFocusedIndex];
        if (safeFocusedIndex >= 0 && selectedOption)
          onSelectAttendeeOption(selectedOption);
      } else if (
        e.key === 'Backspace' &&
        inputValue === '' &&
        attendees.length
      ) {
        const attendeeToRemove =
          safeInlineFocusedIndex < 0 ||
          safeInlineFocusedIndex >= attendees.length
            ? attendees.length - 1
            : safeInlineFocusedIndex;

        const attendeeToRemoveItem = attendees[attendeeToRemove];
        if (attendeeToRemoveItem) {
          attendeesService.removePageAttendee(
            pageId,
            attendeeToRemoveItem.id
          );
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const newFocusedIndex = clamp(
          safeFocusedIndex + (e.key === 'ArrowUp' ? -1 : 1),
          0,
          attendeeOptions.length - 1
        );
        scrollContainerRef.current
          ?.querySelector(
            `.${styles.attendeeSelectorItem}:nth-child(${newFocusedIndex + 1})`
          )
          ?.scrollIntoView({ block: 'nearest' });
        setFocusedIndex(newFocusedIndex);
        // reset inline focus
        setFocusedInlineIndex(attendees.length + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const newItemToFocus =
          e.key === 'ArrowLeft'
            ? safeInlineFocusedIndex - 1
            : safeInlineFocusedIndex + 1;

        e.preventDefault();
        setFocusedInlineIndex(newItemToFocus);
        // reset attendee list focus
        setFocusedIndex(-1);
      }
    },
    [
      attendeeOptions,
      attendees,
      attendeesService,
      inputValue,
      onSelectAttendeeOption,
      pageId,
      potentialEmail,
      potentialName,
      safeInlineFocusedIndex,
      safeFocusedIndex,
    ]
  );

  return (
    <div
      data-testid="attendees-editor-popup"
      className={styles.attendeesEditorRoot}
    >
      <div className={styles.attendeesEditorSelectedAttendees}>
        <InlineAttendeesList
          pageId={pageId}
          readonly={readonly}
          focusedIndex={safeInlineFocusedIndex}
          onRemove={focusInput}
        >
          <input
            ref={inputRef}
            value={inputValue}
            onChange={onInputChange}
            onKeyDown={onInputKeyDown}
            autoFocus
            className={styles.searchInput}
            placeholder="Type email to add..."
          />
        </InlineAttendeesList>
      </div>
      <div className={styles.attendeesEditorSelector}>
        <div className={styles.attendeesEditorSelectorHeader}>Attendees</div>
        <Scrollable.Root>
          <Scrollable.Viewport
            ref={scrollContainerRef}
            className={styles.attendeeSelectorScrollContainer}
          >
            {attendeeOptions.map((attendee, idx) => {
              const commonProps = {
                onClick: () => onSelectAttendeeOption(attendee),
                onMouseEnter: () => setFocusedIndex(idx),
                ['data-testid']: 'attendee-selector-item',
                ['data-focused']: safeFocusedIndex === idx ? 'true' : undefined,
                className: styles.attendeeSelectorItem,
              };

              if (isCreateNewAttendee(attendee)) {
                return (
                  <div key={'create-new-attendee'} {...commonProps}>
                    <TempAttendeeItem
                      name={attendee.name}
                      email={attendee.email}
                    />
                  </div>
                );
              } else {
                const isSelected = attendeeExistsByEmail(
                  attendees,
                  attendee.email
                );

                return (
                  <div
                    key={attendee.id}
                    {...commonProps}
                    data-attendee-id={attendee.id}
                    data-selected={isSelected ? 'true' : undefined}
                  >
                    <AttendeeItem attendee={attendee} />
                    <div className={styles.attendeeDetails}>
                      <div className={styles.attendeeName}>{attendee.name}</div>
                      <div className={styles.attendeeEmail}>
                        {attendee.email}
                      </div>
                    </div>
                    <div className={styles.spacer} />
                    <EditAttendeeMenu attendeeId={attendee.id} pageId={pageId}>
                      <IconButton className={styles.attendeeEditIcon}>
                        <MoreHorizontalIcon />
                      </IconButton>
                    </EditAttendeeMenu>
                  </div>
                );
              }
            })}
          </Scrollable.Viewport>
          <Scrollable.Scrollbar style={{ transform: 'translateX(6px)' }} />
        </Scrollable.Root>
      </div>
    </div>
  );
};

interface AttendeesInlineEditorProps extends AttendeesEditorProps {
  placeholder?: string;
  className?: string;
}

export const AttendeesInlineEditor = ({
  pageId,
  readonly,
}: AttendeesInlineEditorProps) => {
  const attendeesService = useService(AttendeesService);
  const allAttendeesMap = useLiveData(attendeesService.attendees$);
  const attendees = useMemo(
    () => deduplicateAttendees(allAttendeesMap[pageId] || []),
    [allAttendeesMap, pageId]
  );
  const empty = !attendees || attendees.length === 0;

  return (
    <Menu
      contentOptions={{
        side: 'bottom',
        align: 'start',
        sideOffset: 0,
        avoidCollisions: false,
        className: styles.attendeesMenu,
        onClick(e: React.MouseEvent) {
          e.stopPropagation();
        },
      }}
      items={<AttendeesEditor pageId={pageId} readonly={readonly} />}
    >
      <div className={styles.tableHeaderAttendees}>
        <UsersIcon className={styles.tableHeaderCollectionIcon} />
        {!empty && <InlineAttendeesList pageId={pageId} readonly={true} />}
      </div>
    </Menu>
  );
};
